import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { useProductCenterApi } from 'api';
import {
	unitCommissioningDataQuery,
	unitEvcStatusQuery,
	unitKeys,
	unitStructureQuery,
} from 'api/queries';
import { AreaName } from 'domain/constants';
import { isControlSystemStatusCorrect } from 'domain/unit/EVC';
import {
	isCommissioningButtonDisabled,
	showCommissioningSection,
} from 'domain/unit/Unit';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { reactPlugin } from 'main/azureInsights/config';
import { commissioningWizardPageUrl } from 'main/routes/urls/RouteUrls';
import {
	resetState,
	selectCommissioningFiles,
} from 'modules/Commissioning/State';
import {
	attachmentFolderCreated,
	fileDeleted,
	fileUploaded,
} from 'modules/Commissioning/State/FilesSlice';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { isUnitIndustrial, isUnitMarine } from 'modules/Shared/State/unit';
import { Questions } from 'modules/UnitOverview/Commissioning/Components/Questions';
import { getConsentOptions } from 'modules/UnitOverview/Commissioning/Components/consentOptions';
import { AdditionalFileUploadButton } from 'modules/UnitOverview/Components/AdditionalFileUploadButton';
import { AdditionalFilesSection } from 'modules/UnitOverview/Components/AdditionalFilesSection';
import { ChangedByInfoDisplay } from 'modules/UnitOverview/Components/ChangedByInfoDisplay';
import { DateCommentFilesDisplay } from 'modules/UnitOverview/Components/DateCommentFilesDisplay';
import { FilesDisplay } from 'modules/UnitOverview/Components/FilesDisplay';
import { LocalLinkButton } from 'modules/UnitOverview/Components/LocalLinkButton';
import { RadioGroupDisplay } from 'modules/UnitOverview/Components/RadioGroupDisplay';
import { IAdditionalFileUploadStateHandlers } from 'modules/UnitOverview/Components/UploadAdditionalFilesDialog';
import { EVCStatusError } from 'modules/UnitOverview/UnitInformation/Components/EVCStatusError';

const fileStateHandlers: IAdditionalFileUploadStateHandlers = {
	attachmentFolderCreated,
	fileDeleted,
	fileUploaded,
	fileStateSelector: selectCommissioningFiles,
	reset: resetState,
};

const CommissioningSectionPage = (): React.JSX.Element | null => {
	const { t } = useTranslation([
		'common',
		'commonWizard',
		'commissioning',
		'unitOverview',
	]);
	const unitIds = useResolvedUnitIds();
	const consentOptions = useMemo(() => getConsentOptions(t), [t]);

	const queryClient = useQueryClient();
	const { api } = useProductCenterApi();
	const { data: commissioningData } = useSuspenseQuery(
		unitCommissioningDataQuery(api, unitIds.id)
	);
	const { data } = useSuspenseQuery(unitStructureQuery(unitIds.id));
	const { data: evcStatus } = useSuspenseQuery(
		unitEvcStatusQuery(api, unitIds.id)
	);

	const isEVCCorrect = isControlSystemStatusCorrect(evcStatus);

	const isRegistrationButtonDisabled = isCommissioningButtonDisabled(data);
	const isNotConnected = !showCommissioningSection(data);

	const handleOnClose = useCallback(
		(isSubmitting: boolean) => {
			if (isSubmitting) {
				void queryClient.invalidateQueries({
					queryKey: unitKeys.commissioningData(unitIds.id),
				});
			}
		},
		[queryClient, unitIds.id]
	);

	if (isNotConnected) {
		return (
			<AuthorizeComponent
				areaName={AreaName.Commissioning}
				permission={OperationType.Create}>
				{!isEVCCorrect && <EVCStatusError />}
				<Box
					sx={{
						paddingTop: '1em',
					}}>
					<LocalLinkButton
						disabled={isRegistrationButtonDisabled || !isEVCCorrect}
						to={generatePath(commissioningWizardPageUrl, {
							unitId: unitIds.publicId,
						})}>
						{t('unitOverview:perform-commissioning-button')}
					</LocalLinkButton>
				</Box>
			</AuthorizeComponent>
		);
	}

	return !!commissioningData ? (
		<>
			<DateCommentFilesDisplay
				date={commissioningData.commissioningDate}
				dateLabel={t('commonWizard:commissioning-date')}
				comment={commissioningData.comments}
				fileSet={commissioningData.initialFileSet}
				onDelete={handleOnClose}
			/>
			<Box
				sx={{
					display: 'grid',

					gridTemplateColumns: {
						xs: '1fr',
						sm: '1fr 1fr',
					},

					gap: { xs: 2, sm: 4 },
					mb: 6,
				}}>
				{isUnitMarine(data?.mainSegment) && (
					<>
						<Typography>
							{t('commissioning:wasSeaTrialPerformed')}
						</Typography>
						<RadioGroupDisplay
							value={commissioningData.wasSeaTrialPerformed}
							radioLabelsList={consentOptions}
							row={true}
						/>
					</>
				)}
				<Typography>
					{t('unitOverview:commissioning-information-shared-text')}
				</Typography>
				<RadioGroupDisplay
					value={commissioningData.isInformationSharedWithCustomer}
					radioLabelsList={consentOptions}
					row={true}
				/>
			</Box>
			<Typography variant="subtitle2">
				{t('commissioning:commissioning-vodia-readout-label')}
			</Typography>
			<FilesDisplay
				fileSet={commissioningData.vodiaFileSet}
				onDelete={handleOnClose}
			/>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					my: 2,
					pr: { xs: 0, sm: 10 },
				}}>
				<ChangedByInfoDisplay
					label={'unitOverview:commissioned-by'}
					changedByInfo={commissioningData.performedBy}
				/>
			</Box>
			{commissioningData.commissioningQuestions &&
				!isUnitIndustrial(data?.mainSegment) && (
					<Box
						sx={{
							mb: 6,
						}}>
						<Questions
							questions={commissioningData.commissioningQuestions}
						/>
					</Box>
				)}
			<AdditionalFilesSection
				fileSets={commissioningData.additionalFileSets}
				additionalAction={
					<AdditionalFileUploadButton
						handler={fileStateHandlers}
						onClose={handleOnClose}
					/>
				}
				onDelete={handleOnClose}
			/>
		</>
	) : null;
};

export default withAITracking(
	reactPlugin,
	CommissioningSectionPage,
	'UnitOverviewCommissioningSectionPage',
	'application-insight-container'
);
